<template>
  <div class="video-run-row">
    <v-skeleton-loader v-if="loading && !runs.length" type="table-row@3" />

    <v-alert v-else-if="error" type="error" outlined dense class="mb-0">
      <div class="d-flex align-center">
        <span>{{ error }}</span>
        <v-spacer />
        <v-btn small outlined color="error" @click="loadRuns">Retry</v-btn>
      </div>
    </v-alert>

    <div v-else-if="!visibleSections.length" class="video-run-row__empty text-center py-4">
      <v-icon size="36" color="grey lighten-1">mdi-history</v-icon>
      <div class="caption grey--text text--darken-1 mt-1">No runs yet</div>
      <v-btn
        small
        text
        color="primary"
        class="text-none mt-1 video-run-row__launch"
        @click="$emit('launch', item)"
      >
        <v-icon small left>mdi-play-circle-outline</v-icon>
        Launch analysis
      </v-btn>
    </div>

    <template v-else>
      <section
        v-for="section in visibleSections"
        :key="section.key"
        class="video-run-row__section"
        :class="{ 'video-run-row__section--group': section.group_id }"
      >
        <header class="video-run-row__section-header d-flex align-center flex-wrap">
          <v-icon small class="mr-1" :color="section.group_id ? 'primary' : 'grey darken-1'">
            {{ section.group_id ? 'mdi-flask-outline' : 'mdi-account-arrow-right-outline' }}
          </v-icon>
          <router-link
            v-if="section.group_id"
            :to="`/analysis-groups/${section.group_id}`"
            class="video-run-row__group-link subtitle-2"
            :title="`Open group ${section.name}`"
          >
            {{ section.name }}
          </router-link>
          <span v-else class="subtitle-2">{{ section.name }}</span>
          <span class="caption grey--text ml-2">
            {{ section.runs.length }} {{ section.runs.length === 1 ? 'run' : 'runs' }}
          </span>
          <template v-if="section.membership">
            <v-chip
              v-for="chip in resolutionChipsOf(section.membership)"
              :key="chip.key"
              x-small
              label
              :outlined="chip.outlined"
              :color="chip.color"
              :dark="!chip.outlined"
              class="ml-2 video-run-row__resolution"
            >
              <v-icon x-small left>{{ chip.icon }}</v-icon>
              {{ chip.text }}
            </v-chip>
          </template>
          <v-spacer />
          <template v-if="section.membership">
            <v-btn
              v-if="section.membership.pinned_run_id"
              x-small
              text
              color="info"
              class="text-none video-run-row__unpin-group"
              :loading="busyGroupId === section.group_id"
              @click="pinInGroup(section, null)"
            >
              <v-icon x-small left>mdi-pin-off-outline</v-icon>
              Unpin in group
            </v-btn>
            <v-btn
              x-small
              text
              color="error"
              class="text-none video-run-row__remove-group"
              :loading="busyGroupId === section.group_id"
              @click="removeFromGroup(section)"
            >
              <v-icon x-small left>mdi-minus-circle-outline</v-icon>
              Remove from group
            </v-btn>
          </template>
        </header>

        <v-simple-table v-if="section.runs.length" dense class="video-run-row__table">
          <template #default>
            <thead>
              <tr>
                <th>Run</th>
                <th>Batch</th>
                <th>Versions</th>
                <th>Result</th>
                <th>Status</th>
                <th>Started</th>
                <th>Duration</th>
                <th class="text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="entry in rowsOf(section)"
                :key="entry.id"
                class="video-run-row__run"
                :class="{ 'video-run-row__run--active': entry.actions.isActive }"
                :data-run-id="entry.id"
              >
                <td class="text-no-wrap">
                  <span class="font-weight-medium">{{ runTitle(entry.run) }}</span>
                  <v-icon
                    v-if="entry.actions.isActive"
                    x-small
                    color="success"
                    class="ml-1"
                    title="Active run"
                  >
                    mdi-star
                  </v-icon>
                  <v-icon
                    v-if="entry.actions.isActive && activePinned"
                    x-small
                    color="info"
                    class="ml-1"
                    title="Pinned"
                  >
                    mdi-pin
                  </v-icon>
                  <v-icon
                    v-if="entry.actions.isGroupPinned"
                    x-small
                    color="info"
                    class="ml-1"
                    title="Pinned in this group"
                  >
                    mdi-pin
                  </v-icon>
                </td>
                <td class="text-truncate video-run-row__batch" :title="entry.run.note || ''">
                  {{ entry.run.batch || '—' }}
                </td>
                <td><RunVersionChips :run="entry.run" :source-type="sourceType" dense /></td>
                <td class="text-no-wrap">{{ resultOf(entry.run) }}</td>
                <td>
                  <RunStatusChip :status="entry.run.status" x-small />
                  <span
                    v-if="isLive(entry.run) && entry.run.progress"
                    class="caption grey--text ml-1"
                  >
                    {{ Math.round(entry.run.progress) }}%
                  </span>
                </td>
                <td class="text-no-wrap">
                  {{ formatDateTime(entry.run.started_at || entry.run.enqueued_at) }}
                </td>
                <td class="text-no-wrap">{{ durationOf(entry.run) }}</td>
                <td class="text-right text-no-wrap">
                  <v-btn
                    icon
                    x-small
                    title="Open this run"
                    class="video-run-row__open"
                    :disabled="!entry.actions.open"
                    @click="open(entry.run)"
                  >
                    <v-icon small>mdi-open-in-new</v-icon>
                  </v-btn>
                  <v-btn
                    v-if="entry.actions.compare"
                    icon
                    x-small
                    title="Compare with the active run"
                    class="video-run-row__compare"
                    @click="compare(entry.run)"
                  >
                    <v-icon small>mdi-compare-horizontal</v-icon>
                  </v-btn>
                  <v-btn
                    v-if="entry.actions.promote"
                    icon
                    x-small
                    color="success"
                    title="Promote to active run"
                    class="video-run-row__promote"
                    :loading="busyRunId === entry.id"
                    @click="pointerAction('promote', entry.run)"
                  >
                    <v-icon small>mdi-star-outline</v-icon>
                  </v-btn>
                  <v-btn
                    v-if="entry.actions.unpin"
                    icon
                    x-small
                    color="info"
                    title="Unpin (later runs may become active again)"
                    class="video-run-row__unpin"
                    :loading="busyRunId === entry.id"
                    @click="pointerAction('unpin', entry.run)"
                  >
                    <v-icon small>mdi-pin-off</v-icon>
                  </v-btn>
                  <v-btn
                    v-else-if="entry.actions.pin"
                    icon
                    x-small
                    title="Pin as active run (blocks automatic promotion)"
                    class="video-run-row__pin"
                    :loading="busyRunId === entry.id"
                    @click="pointerAction('pin', entry.run)"
                  >
                    <v-icon small>mdi-pin-outline</v-icon>
                  </v-btn>
                  <v-btn
                    v-if="entry.actions.pinInGroup && section.membership"
                    icon
                    x-small
                    color="info"
                    title="Pin in this group (evaluated run of the group)"
                    class="video-run-row__pin-group"
                    :loading="busyGroupId === section.group_id"
                    @click="pinInGroup(section, entry.id)"
                  >
                    <v-icon small>mdi-pin-outline</v-icon>
                  </v-btn>
                  <v-btn
                    v-if="entry.actions.cancel"
                    icon
                    x-small
                    color="warning"
                    title="Cancel run"
                    class="video-run-row__cancel"
                    :loading="busyRunId === entry.id"
                    @click="liveAction('cancel', entry.run)"
                  >
                    <v-icon small>mdi-close-circle-outline</v-icon>
                  </v-btn>
                  <v-btn
                    v-if="entry.actions.remove"
                    icon
                    x-small
                    color="warning"
                    title="Remove from the queue"
                    class="video-run-row__remove"
                    :loading="busyRunId === entry.id"
                    @click="liveAction('remove', entry.run)"
                  >
                    <v-icon small>mdi-tray-remove</v-icon>
                  </v-btn>
                  <v-btn
                    v-if="isLive(entry.run)"
                    icon
                    x-small
                    title="Open analysis queue"
                    to="/analysis-queue"
                  >
                    <v-icon small>mdi-tray-full</v-icon>
                  </v-btn>
                </td>
              </tr>
            </tbody>
          </template>
        </v-simple-table>
        <p v-else class="caption grey--text mb-0 pl-6 video-run-row__no-runs">
          No runs in this group yet.
        </p>
      </section>
    </template>
  </div>
</template>

<script>
import RunStatusChip from '@/components/analysis/runs/RunStatusChip.vue';
import RunVersionChips from '@/components/analysis/runs/RunVersionChips.vue';
import { describeActionError } from '@/components/queue/queueFormat';
import AnalysisGroupsService from '@/services/AnalysisGroupsService';
import AnalysisRunsService from '@/services/AnalysisRunsService';
import SocketioService from '@/services/socketioService';
import { resolutionChips, sourceRouteWithRun } from '@/utils/groupResolution';
import { runIdOf } from '@/utils/runLabels';
import {
  durationOf,
  formatDateTime,
  groupRunsBySection,
  isLiveStatus,
  isSucceeded,
  resultOf,
  runActionsFor,
  runTitle,
} from './runRowHelpers';

const SOCKET_EVENT = 'analysisJobsChanged';
const SOCKET_REFRESH_DEBOUNCE_MS = 600;

/**
 * Runs of one source (manual video or interview) grouped by analysis group (M4 F13 / F19):
 * direct runs first, then one section per group with its resolution chips, 'Pin in group' /
 * 'Unpin in group', 'Remove from group' and a link to the group. Per run: version chips,
 * status, Open (`?run=`), Compare, Promote / Pin / Unpin, Cancel / Remove from the queue.
 *
 * Loads its runs on creation (it is only mounted when the row is expanded) and refreshes them
 * when the queue socket reports a change for this source.
 *
 * Emits: `compare` `{ a, b }` (run ids), `changed` `{ action, run_id }` after a pointer or
 * queue action, `membership-changed` `{ group_id, action }`, `notify` `{ message, type }`,
 * `launch` (item) from the empty state.
 */
export default {
  name: 'VideoRunRow',

  components: { RunStatusChip, RunVersionChips },

  props: {
    /** Source row: at least `{ id, name? }`. */
    item: { type: Object, required: true },
    /** 'manual_analysis' | 'interview'. */
    sourceType: { type: String, default: 'manual_analysis' },
    /** `GET /analysis-runs/summary` item of the source (active pointer). */
    summary: { type: Object, default: null },
    /** Membership entries of the source (`GET /analysis-groups/membership` groups). */
    memberships: { type: Array, default: () => [] },
    /** Every analysis group (names for runs whose membership is gone). */
    groups: { type: Array, default: () => [] },
  },

  data() {
    return {
      runs: [],
      loading: false,
      error: '',
      busyRunId: null,
      busyGroupId: null,
      socketTimer: null,
    };
  },

  computed: {
    sourceId() {
      return this.item?.id ?? null;
    },
    activeRunId() {
      return this.summary?.active_run_id ? String(this.summary.active_run_id) : null;
    },
    activePinned() {
      return this.summary?.active_run_pinned === true;
    },
    sections() {
      return groupRunsBySection(this.runs, {
        memberships: this.memberships,
        groups: this.groups,
      });
    },
    visibleSections() {
      return this.sections.filter((section) => section.runs.length || section.membership);
    },
    succeededCount() {
      return this.runs.filter(isSucceeded).length;
    },
  },

  created() {
    this.loadRuns();
    SocketioService.emisor.$on(SOCKET_EVENT, this.onJobsChanged);
  },

  beforeDestroy() {
    SocketioService.emisor.$off(SOCKET_EVENT, this.onJobsChanged);
    if (this.socketTimer) clearTimeout(this.socketTimer);
  },

  methods: {
    runTitle,
    resultOf,
    durationOf,
    formatDateTime,

    isLive(run) {
      return isLiveStatus(run?.status);
    },

    resolutionChipsOf(membership) {
      return resolutionChips(membership?.resolution || null);
    },

    rowsOf(section) {
      return section.runs.map((run) => ({
        id: runIdOf(run),
        run,
        actions: runActionsFor(run, {
          activeRunId: this.activeRunId,
          activePinned: this.activePinned,
          groupPinnedRunId: section.membership?.pinned_run_id ?? null,
          succeededCount: this.succeededCount,
        }),
      }));
    },

    async loadRuns() {
      if (this.sourceId == null) return;
      this.loading = true;
      this.error = '';
      try {
        const response = await AnalysisRunsService.list({
          source_type: this.sourceType,
          source_id: this.sourceId,
          limit: 100,
        });
        const payload = response?.data?.data ?? response?.data ?? {};
        this.runs = Array.isArray(payload.items)
          ? payload.items
          : Array.isArray(payload)
            ? payload
            : [];
      } catch (error) {
        this.runs = [];
        this.error = 'Could not load the runs of this video.';
      } finally {
        this.loading = false;
      }
    },

    onJobsChanged(event) {
      if (!event || event.source_type !== this.sourceType) return;
      if (String(event.source_id ?? '') !== String(this.sourceId ?? '')) return;
      if (this.socketTimer) clearTimeout(this.socketTimer);
      this.socketTimer = setTimeout(() => {
        this.socketTimer = null;
        this.loadRuns();
      }, SOCKET_REFRESH_DEBOUNCE_MS);
    },

    open(run) {
      const route = sourceRouteWithRun(
        { source_type: this.sourceType, source_id: this.sourceId },
        runIdOf(run)
      );
      if (route) this.$router.push(route);
    },

    /** Compare against the active run when it is another SUCCEEDED run, else the newest other one. */
    compare(run) {
      const id = runIdOf(run);
      const others = this.runs.filter((other) => isSucceeded(other) && runIdOf(other) !== id);
      if (!others.length) return;
      const active = others.find((other) => runIdOf(other) === this.activeRunId);
      const counterpart = active || others[0];
      this.$emit('compare', { a: id, b: runIdOf(counterpart) });
    },

    async pointerAction(action, run) {
      const id = runIdOf(run);
      if (!id || this.busyRunId) return;
      this.busyRunId = id;
      try {
        if (action === 'promote') await AnalysisRunsService.promote(id);
        else if (action === 'pin') await AnalysisRunsService.pin(id);
        else if (action === 'unpin') await AnalysisRunsService.unpin(id);
        this.$emit('changed', { action, run_id: id });
        this.$emit('notify', { message: this.successMessage(action), type: 'success' });
        await this.loadRuns();
      } catch (error) {
        this.$emit('notify', { message: describeActionError(error, action), type: 'error' });
      } finally {
        this.busyRunId = null;
      }
    },

    /** RUNNING -> cancel; QUEUED | DISPATCHED -> remove. Through the queue store when it is registered. */
    async liveAction(action, run) {
      const id = runIdOf(run);
      if (!id || this.busyRunId) return;
      this.busyRunId = id;
      const storeAction = `analysisQueue/${action}`;
      try {
        if (this.$store?._actions?.[storeAction]) await this.$store.dispatch(storeAction, id);
        else if (action === 'cancel') await AnalysisRunsService.cancel(id);
        else await AnalysisRunsService.remove(id);
        this.$emit('changed', { action, run_id: id });
        this.$emit('notify', { message: this.successMessage(action), type: 'success' });
        await this.loadRuns();
      } catch (error) {
        this.$emit('notify', { message: describeActionError(error, action), type: 'error' });
      } finally {
        this.busyRunId = null;
      }
    },

    async pinInGroup(section, runId) {
      const membership = section?.membership;
      if (!membership || membership.member_id == null || this.busyGroupId) return;
      this.busyGroupId = section.group_id;
      try {
        await AnalysisGroupsService.pinMember(section.group_id, membership.member_id, runId);
        this.$emit('membership-changed', {
          group_id: section.group_id,
          action: runId ? 'pin' : 'unpin',
        });
        this.$emit('notify', {
          message: runId ? `Run pinned in ${section.name}.` : `Run unpinned in ${section.name}.`,
          type: 'success',
        });
      } catch (error) {
        this.$emit('notify', {
          message: describeActionError(error, runId ? 'pin' : 'unpin'),
          type: 'error',
        });
      } finally {
        this.busyGroupId = null;
      }
    },

    async removeFromGroup(section) {
      const membership = section?.membership;
      if (!membership || membership.member_id == null || this.busyGroupId) return;
      this.busyGroupId = section.group_id;
      try {
        await AnalysisGroupsService.removeMember(section.group_id, membership.member_id);
        this.$emit('membership-changed', { group_id: section.group_id, action: 'remove' });
        this.$emit('notify', {
          message: `Removed from ${section.name}. Its runs in the group are kept.`,
          type: 'success',
        });
      } catch (error) {
        this.$emit('notify', { message: describeActionError(error, 'remove'), type: 'error' });
      } finally {
        this.busyGroupId = null;
      }
    },

    successMessage(action) {
      if (action === 'promote') return 'Run promoted to active run.';
      if (action === 'pin') return 'Run pinned.';
      if (action === 'unpin') return 'Run unpinned.';
      if (action === 'cancel') return 'Cancellation requested.';
      if (action === 'remove') return 'Run removed from the queue.';
      return 'Done.';
    },
  },
};
</script>

<style scoped>
.video-run-row {
  padding: 8px 0 4px;
}

.video-run-row__section + .video-run-row__section {
  margin-top: 12px;
}

.video-run-row__section-header {
  padding: 4px 8px;
  row-gap: 4px;
}

.video-run-row__section--group .video-run-row__section-header {
  background: rgba(98, 0, 234, 0.05);
  border-radius: 6px;
}

.video-run-row__group-link {
  text-decoration: none;
  color: #6200ea;
}

.video-run-row__group-link:hover {
  text-decoration: underline;
}

.video-run-row__batch {
  max-width: 180px;
}

.video-run-row__run--active td:first-child {
  box-shadow: inset 3px 0 0 #2e7d32;
}
</style>
